import Lenis from '@studio-freight/lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

/**
 * Initialize smooth scrolling with Lenis
 * Sets up GSAP ScrollTrigger integration
 * @returns {Lenis} The Lenis instance
 */
export const initSmoothScroll = () => {
  // Create Lenis smooth scrolling instance
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential ease out
    direction: 'vertical',
    gestureDirection: 'vertical',
    smooth: true,
    mouseMultiplier: 1,
    smoothTouch: false,
    touchMultiplier: 2,
    infinite: false,
  });

  // Connect Lenis to GSAP ScrollTrigger
  lenis.on('scroll', ScrollTrigger.update);

  // Update ScrollTrigger on Lenis scroll
  const updateScrollTrigger = (time) => {
    lenis.raf(time * 1000);
  };

  // Add Lenis RAF callback to GSAP ticker
  gsap.ticker.add(updateScrollTrigger);

  // Remove callback when unmounting
  const cleanup = () => {
    gsap.ticker.remove(updateScrollTrigger);
    lenis.destroy();
  };

  return { lenis, cleanup };
};

/**
 * Initialize reveal animations for elements with the 'reveal' class
 * Elements will animate in when they enter the viewport
 */
export const initRevealAnimations = () => {
  // Get all elements with the 'reveal' class
  const revealElements = document.querySelectorAll('.reveal');

  // Create ScrollTrigger for each reveal element
  revealElements.forEach((element) => {
    ScrollTrigger.create({
      trigger: element,
      start: 'top 85%',
      onEnter: () => element.classList.add('active'),
      once: true
    });
  });
};

/**
 * Initialize parallax effects for elements with the 'parallax' class
 * Elements will move at different speeds when scrolling
 */
export const initParallaxEffects = () => {
  // Get all elements with the 'parallax' class
  const parallaxElements = document.querySelectorAll('.parallax');

  // Create parallax effect for each element
  parallaxElements.forEach((element) => {
    const speed = element.dataset.speed || 0.1;
    const direction = element.dataset.direction || 'up';
    const factor = direction === 'up' ? -1 : 1;

    gsap.to(element, {
      y: () => factor * speed * ScrollTrigger.maxScroll(window),
      ease: 'none',
      scrollTrigger: {
        trigger: element.parentElement,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true
      }
    });
  });
};

/**
 * Initialize horizontal scroll sections
 * @param {string} selector - CSS selector for horizontal scroll containers
 */
export const initHorizontalScroll = (selector = '.horizontal-scroll') => {
  const sections = document.querySelectorAll(selector);

  sections.forEach((section) => {
    const container = section.querySelector('.horizontal-scroll-container');
    
    if (!container) return;
    
    const width = container.scrollWidth;
    
    gsap.to(container, {
      x: () => -(width - window.innerWidth),
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => `+=${width}`,
        scrub: true,
        pin: true,
        invalidateOnRefresh: true
      }
    });
  });
};
